import {RecipeNode} from '../api/types.js'

//  ======================================================
// TYPE DEFINITIONS
// ======================================================

export type NodeType = 'ingredient' | 'transformation' | 'combined';
export type Vector2D = { x: number; y: number };

export enum EditionMode {
  NONE = 'none',
  EDIT = 'edit',
  ADD_PARENT_LINK = 'add-parent-link',
  ADD_CHILD_LINK = 'add-child-link',
  DELETE_PARENT_LINK = 'delete-parent-link',
  DELETE_CHILD_LINK = 'delete-child-link',
  DRAG_CREATE = 'drag-create',
  PICK_NODE = 'pick-node',
}

// Interface for objects with the attributes needed by the physics simulation
export interface SimulationNode {
  // Current position (can be modified by forces)
  x?: number;
  y?: number;
  // Fixed position (forces ignore this)
  fx?: number | null;
  fy?: number | null;
  // Velocity (calculated by simulation)
  vx?: number;
  vy?: number;
}

export interface DisplayNode extends SimulationNode {
  id: string;                    // Unique identifier for node
  name: string;                  // Display name for node
  type: NodeType;                // Classification for styling
  data: RecipeNode;              // Original recipe node data
  recipeId: string;              // Which recipe this node belongs to
  isMainRoot: boolean;           // Is this the main root of its recipe?
  leafDistance: number;          // Distance to leaf nodes (0 = leaf nodes)
  rootDistance: number;          // Distance to root node (0 = recipe itself)
  __inactive?: boolean;          // Whether to draw this node as inactive (not eligible for current operation)
}

export interface SimulationLink<T> {
  source: T;  // Source node identifier or object (parent)
  target: T;  // Target node identifier or object (child)
}

export interface Link<T> extends SimulationLink<T> {
  shape: Vector2D;  // Vector defining the rigid link
  active: boolean;  // Whether the link is active for strength computation
}

/**
 * Assign a straight-line shape to a link from its endpoints' current
 * positions — the one implementation shared by RecipeStore, DisplayGraph,
 * and GraphVisualizer (previously three identical copies).
 */
export function assignLinkShape(link: Link<DisplayNode>): void {
  const source = link.source;
  const target = link.target;
  if (source?.x != null && source?.y != null && target?.x != null && target?.y != null) {
    link.shape = { x: target.x - source.x, y: target.y - source.y };
  }
}


